import React, { useEffect, useMemo, useState } from "react";
import {
  FaAddressBook,
  FaEnvelope,
  FaPhone,
  FaUsers,
  FaUserTie,
} from "react-icons/fa";

import useScrollToTop from "../../../hooks/useScrollToTop";
import Reveal from "../../../components/ui/Reveal";

import { getAllTeamMembers } from "../../../services/teamServices";

import FormContact from "./../HomePage/FormContact";
import SectionContacts from "./SectionContacts";
import SectionTitle from "./../HomePage/SectionTitle";
import Footer from "./../HomePage/Footer";

import { resolveMediaUrl } from "./../HomePage/homeUtils";

const ContactPage = () => {
  useScrollToTop();

  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const fetchTeamMembers = async () => {
      try {
        setLoading(true);

        const data = await getAllTeamMembers();

        const sortedTeamMembers = Array.isArray(data)
          ? [...data].sort((a, b) => {
              const positionA = Number(a.position ?? 999);
              const positionB = Number(b.position ?? 999);

              if (positionA !== positionB) return positionA - positionB;

              return `${a.nom || ""} ${a.prenom || ""}`.localeCompare(
                `${b.nom || ""} ${b.prenom || ""}`,
                "fr",
                { sensitivity: "base" },
              );
            })
          : [];

        if (mounted) setTeamMembers(sortedTeamMembers);
      } catch (error) {
        console.error(
          "Erreur lors de la récupération des membres de l'équipe:",
          error,
        );
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchTeamMembers();

    return () => {
      mounted = false;
    };
  }, []);

  const stats = useMemo(() => {
    return [
      {
        icon: FaUsers,
        label: "Responsables",
        value: teamMembers.length,
      },
      {
        icon: FaAddressBook,
        label: "Service",
        value: "Vie associative",
      },
      {
        icon: FaUserTie,
        label: "Accompagnement",
        value: "Mairie",
      },
    ];
  }, [teamMembers.length]);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-white">
      <div className="h-[70px] w-full bg-slate-950" />

      <main>
        <section className="relative overflow-hidden bg-slate-950 py-20 sm:py-24 lg:py-28">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(34,197,94,0.22),transparent_35%),radial-gradient(circle_at_80%_35%,rgba(59,130,246,0.24),transparent_40%)]" />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-950/90 to-slate-900" />

          <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid items-center gap-10 lg:grid-cols-[1.1fr_0.9fr]">
              <Reveal>
                <div>
                  <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm font-bold text-white/90 backdrop-blur">
                    <FaUserTie />
                    Mairie & associations
                  </span>

                  <h1 className="mt-6 text-4xl font-bold leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
                    Responsables sports & associations
                  </h1>

                  <p className="mt-6 max-w-3xl text-base leading-8 text-white/75 sm:text-lg">
                    Retrouvez les interlocuteurs de la mairie en charge du
                    sport, des associations et de la vie locale.
                  </p>
                </div>
              </Reveal>

              <Reveal delay={0.12}>
                <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-1">
                  {stats.map((stat) => (
                    <HeroStat
                      key={stat.label}
                      icon={stat.icon}
                      label={stat.label}
                      value={stat.value}
                    />
                  ))}
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        <section className="bg-white py-16 dark:bg-slate-900 sm:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <SectionTitle
              eyebrow="Équipe"
              title="Vos interlocuteurs "
              highlight="à la mairie"
              subtitle="Une équipe disponible pour accompagner les associations, les responsables et les habitants."
            />

            {loading ? (
              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-10 text-center text-slate-500 dark:border-white/10 dark:bg-slate-950 dark:text-slate-300">
                Chargement des responsables...
              </div>
            ) : teamMembers.length === 0 ? (
              <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center text-slate-500 dark:border-white/10 dark:bg-slate-950 dark:text-slate-300">
                Aucun responsable n’est affiché pour le moment.
              </div>
            ) : (
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {teamMembers.map((member, index) => (
                  <Reveal key={member.id || index} delay={index * 0.04}>
                    <TeamMemberCard member={member} />
                  </Reveal>
                ))}
              </div>
            )}
          </div>
        </section>

        <SectionContacts sectionId={1} />

        <div id="contact">
          <FormContact />
        </div>

        <Footer />
      </main>
    </div>
  );
};

function HeroStat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-3xl border border-white/15 bg-white/10 p-5 shadow-2xl backdrop-blur-md">
      <div className="flex items-start gap-4">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white text-primary shadow">
          <Icon />
        </span>

        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-wide text-white/55">
            {label}
          </p>
          <p className="mt-1 truncate text-lg font-bold text-white">{value}</p>
        </div>
      </div>
    </div>
  );
}

function TeamMemberCard({ member }) {
  const fullName = `${member.prenom || ""} ${member.nom || ""}`.trim();
  const photoUrl = resolveMediaUrl(member.photoURL, "/images/user_avatar.jpg");

  return (
    <article className="group h-full overflow-hidden rounded-[2rem] border border-slate-200 bg-slate-50 shadow-sm transition hover:-translate-y-1 hover:shadow-2xl dark:border-white/10 dark:bg-slate-950">
      <div className="relative h-28 bg-slate-950">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(34,197,94,0.35),transparent_35%),radial-gradient(circle_at_75%_40%,rgba(59,130,246,0.35),transparent_40%)]" />

        <div className="absolute left-1/2 top-full flex h-32 w-32 -translate-x-1/2 -translate-y-1/2 items-center justify-center overflow-hidden rounded-[2rem] border-4 border-white bg-white p-1 shadow-xl dark:border-slate-950">
          <img
            src={photoUrl}
            alt={fullName || "Responsable"}
            className="h-full w-full rounded-[1.5rem] object-cover"
          />
        </div>
      </div>

      <div className="flex h-full flex-col items-center px-6 pb-6 pt-20 text-center">
        <h3 className="text-2xl font-bold text-slate-950 dark:text-white">
          {fullName || "Responsable"}
        </h3>

        {member.fonction && (
          <p className="mt-3 rounded-full bg-primary/10 px-4 py-2 text-sm font-bold text-primary">
            {member.fonction}
          </p>
        )}

        {(member.email || member.mail || member.telephone || member.tel) && (
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            {(member.telephone || member.tel) && (
              <a
                href={`tel:${String(member.telephone || member.tel).replace(/[^\d+]/g, "")}`}
                className="inline-flex items-center gap-2 rounded-2xl bg-slate-950 px-4 py-2 text-sm font-bold text-white transition hover:-translate-y-0.5 dark:bg-white dark:text-slate-950"
              >
                <FaPhone />
                Appeler
              </a>
            )}

            {(member.email || member.mail) && (
              <a
                href={`mailto:${member.email || member.mail}`}
                className="inline-flex items-center gap-2 rounded-2xl bg-primary px-4 py-2 text-sm font-bold text-white transition hover:-translate-y-0.5"
              >
                <FaEnvelope />
                Email
              </a>
            )}
          </div>
        )}
      </div>
    </article>
  );
}

export default ContactPage;
